import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Plus, RefreshCw, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { LocalizedName } from '@/components/localized-name';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import { EditVoucherProductSheet } from './edit-voucher-product-sheet';
import { useVoucherProducts } from './vouchers.api';
import { restrictionLines, validityLabel } from './voucher-format';
import type { VoucherProduct } from './types';

/**
 * Gift vouchers — the catalogue customers buy from on the "Gift a voucher"
 * page. Not the vouchers themselves: who bought what, and who holds it, is a
 * separate management screen.
 *
 * The API returns the whole catalogue in one call, so filtering is client-side,
 * like Membership Plans.
 */

const ANY = 'ANY';

export function VoucherProductsPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const { data, isPending, isError, isFetching, refetch } =
    useVoucherProducts();

  const [status, setStatus] = useState(ANY);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<VoucherProduct | null>(null);
  const [creating, setCreating] = useState(false);

  const products = useMemo(() => data ?? [], [data]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return products.filter((product) => {
      if (status !== ANY && String(product.isActive) !== status) return false;
      if (!term) return true;
      return [
        product.nameEn,
        product.nameEs,
        product.descriptionEn,
        product.descriptionEs,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [products, status, query]);

  const isFiltered = status !== ANY || query !== '';

  const filterFields: FilterField[] = [
    {
      id: 'status',
      label: t('vouchers.status'),
      value: status,
      onChange: setStatus,
      sort: false,
      options: [
        { value: ANY, label: t('vouchers.any') },
        { value: 'true', label: t('vouchers.onSale') },
        { value: 'false', label: t('vouchers.offSale') },
      ],
    },
  ];

  const statusBadge = (product: VoucherProduct) => (
    <Badge variant={product.isActive ? 'success' : 'secondary'}>
      {product.isActive ? t('vouchers.onSale') : t('vouchers.offSale')}
    </Badge>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('vouchers.title')}
        description={t('vouchers.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={cn(isFetching && 'animate-spin')} />
              <span className="hidden sm:inline">{t('common.reload')}</span>
            </Button>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus />
              {t('vouchers.createProduct')}
            </Button>
          </div>
        }
      />

      <FilterBar
        busy={isFetching}
        search={
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('vouchers.searchPlaceholder')}
              aria-label={t('common.search')}
              className="h-10 pl-9"
            />
          </div>
        }
        fields={filterFields}
        summary={
          <div className="flex items-center gap-3">
            <p className="text-muted-foreground text-sm">
              {t('vouchers.showingOf', {
                shown: filtered.length,
                total: products.length,
              })}
            </p>
            {isFiltered && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground h-7 text-xs"
                onClick={() => {
                  setStatus(ANY);
                  setQuery('');
                }}
              >
                {t('orders.filters.reset')}
              </Button>
            )}
          </div>
        }
      />

      {isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('vouchers.loadFailed')}</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      )}

      {data && filtered.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={isFetching} />
          <p
            className={cn(
              'text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm',
              isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {products.length === 0
              ? t('vouchers.noProducts')
              : t('vouchers.noMatches')}
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <>
          <div className="hidden rounded-xl border lg:block">
            <Table busy={isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('vouchers.name')}</TableHead>
                  <TableHead>{t('vouchers.restrictions')}</TableHead>
                  <TableHead className="text-right">
                    {t('vouchers.uses')}
                  </TableHead>
                  <TableHead>{t('vouchers.validity')}</TableHead>
                  <TableHead className="text-right">
                    {t('vouchers.price')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('vouchers.issued')}
                  </TableHead>
                  <TableHead>{t('vouchers.status')}</TableHead>
                  <TableHead className="text-right">
                    {t('vouchers.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <LocalizedName
                        nameEn={product.nameEn}
                        nameEs={product.nameEs}
                      />
                    </TableCell>
                    <TableCell className="text-muted-foreground max-w-xs text-xs">
                      {restrictionLines(product.restrictions, language, t).map(
                        (line) => (
                          <p key={line} className="truncate" title={line}>
                            {line}
                          </p>
                        )
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {product.uses}
                    </TableCell>
                    <TableCell>{validityLabel(product.validityAmount, product.validityUnit, t)}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatCurrency(product.price)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {product.vouchersIssued}
                    </TableCell>
                    <TableCell>{statusBadge(product)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditing(product)}
                        >
                          {t('vouchers.edit')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative space-y-2 lg:hidden">
            <BusyOverlay show={isFetching} />
            {filtered.map((product) => (
              <div
                key={product.id}
                className={cn(
                  'bg-card space-y-3 rounded-xl border p-3',
                  isFetching && 'opacity-60 transition-opacity'
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <LocalizedName
                      nameEn={product.nameEn}
                      nameEs={product.nameEs}
                    />
                    <p className="text-muted-foreground text-xs">
                      {t('vouchers.usesCount', { count: product.uses })} ·{' '}
                      {validityLabel(product.validityAmount, product.validityUnit, t)}
                    </p>
                  </div>
                  <span className="shrink-0 font-semibold tabular-nums">
                    {formatCurrency(product.price)}
                  </span>
                </div>

                <div className="text-muted-foreground space-y-0.5 text-xs">
                  {restrictionLines(product.restrictions, language, t).map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {statusBadge(product)}
                  <span className="text-muted-foreground text-xs">
                    {t('vouchers.issued')}: {product.vouchersIssued}
                  </span>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setEditing(product)}
                >
                  {t('vouchers.edit')}
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      <EditVoucherProductSheet
        product={editing}
        open={editing !== null || creating}
        onOpenChange={(next) => {
          if (!next) {
            setEditing(null);
            setCreating(false);
          }
        }}
      />
    </div>
  );
}
